import { prefix } from '@util/imagePrefix';
import { getBikeFitment } from 'app/product/utils/getBikeFitment';
import { ProductDocument } from 'models/product';
import { UserBike } from 'models/shared';
import { X, AlertTriangle } from 'lucide-react';
import { CheckCircle } from '@c/icons/CheckCircle';
import { Tooltip } from 'react-tooltip';
import SafeImage from '@ui/SafeImage';

type GarageBikeCardProps = {
  bike: UserBike;
  product: ProductDocument;
};

export default function GarageBikeCard({ bike, product }: GarageBikeCardProps) {
  const parsedBrandName =
    bike.make === 'Gas Gas' // gas gas needs a special edge case to work
      ? bike.make.split(' ').join('').toLowerCase()
      : bike.make.split(' ').join('-').toLowerCase();

  const brandImage = `${prefix}/assets/brands/${parsedBrandName}_1920x2560.png`;

  const fitment = getBikeFitment(
    product?.fitting_bikes?.bikes ?? [],
    [bike],
    !!product.integration_info
  );

  const getFitmentSymbol = () => {
    const tooltipId = `fitment-tooltip-${bike.make}-${bike.model}-${bike.year}`;
    const tooltipMessage = `The product ${product.title} ${
      fitment?.status === 'fits'
        ? 'fits'
        : fitment?.status === 'may-fit'
        ? 'may fit'
        : "doesn't fit"
    } your bike ${bike.year} ${bike.make} ${bike.model}`;

    if (fitment?.status === 'fits') {
      return (
        <div
          className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1E7A1E] hover:cursor-help"
          data-tooltip-id={tooltipId}
          data-tooltip-content={tooltipMessage}
        >
          <CheckCircle className="h-6 w-6" fill="white" />
          <Tooltip id={tooltipId} />
        </div>
      );
    }
    if (fitment?.status === 'may-fit') {
      return (
        <div
          className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFB800] hover:cursor-help"
          data-tooltip-id={tooltipId}
          data-tooltip-content={tooltipMessage}
        >
          <AlertTriangle className="h-6 w-6 text-white" />
          <Tooltip id={tooltipId} />
        </div>
      );
    }
    return (
      <div
        className={`flex h-10 w-10 items-center justify-center rounded-full ${
          fitment?.status === 'does-not-fit'
            ? 'bg-[#CF2929]'
            : 'bg-[#7F7F7F]/20'
        } hover:cursor-help`}
        data-tooltip-id={tooltipId}
        data-tooltip-content={tooltipMessage}
      >
        <X
          className={`h-6 w-6 ${
            fitment?.status === 'does-not-fit' ? 'text-white' : 'text-[#7F7F7F]'
          }`}
        />
        <Tooltip id={tooltipId} />
      </div>
    );
  };

  return (
    <div className="flex h-[7rem] w-fit min-w-[36rem] items-center justify-between gap-x-8 rounded-[2rem] border border-zinc-200 bg-white px-6">
      <div className="flex items-center gap-x-6">
        <SafeImage
          alt={bike.make}
          src={brandImage}
          fallback={<strong>{bike.make}</strong>}
          height={200}
          width={200}
          className="h-[6rem] w-[6rem] object-contain"
        />

        <div className="flex flex-col gap-y-0">
          <span className="text-[1.4rem] font-medium text-black">
            {bike.year} {bike.make} {bike.model}
          </span>

          <span
            className={`text-[1.4rem] font-medium ${
              fitment?.status === 'fits'
                ? 'text-[#1E7A1E]'
                : fitment?.status === 'may-fit'
                ? 'text-[#FFB800]'
                : fitment?.status === 'does-not-fit'
                ? 'text-[#CF2929]'
                : 'text-[#7F7F7F]'
            }`}
          >
            {fitment?.status === 'fits'
              ? 'Fits your vehicle'
              : fitment?.status === 'may-fit'
              ? 'May fit your vehicle'
              : "Doesn't fit"}
          </span>
        </div>
      </div>

      {getFitmentSymbol()}
    </div>
  );
}
